import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { useMatch } from 'react-router-dom';
import type {
  ContentListObligationsUnderOutput,
  ObligationRole,
  ObligationRollup,
} from '@vivport/backend/types';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  MESSAGE_HIGHLIGHT_QUOTE_EVENT,
  MESSAGE_OPEN_OBLIGATIONS_EVENT,
  type MessageHighlightQuoteDetail,
} from '@/components/widget/message-events';
import { KIND_CLASSES } from '@/components/widget/obligation-kind';
import { ObligationRoleIcon } from '@/components/widget/obligation-role-icons';
import { useObligationsUnder } from '@/hooks/useObligationQueries';
import { useNavigateWithContentFlush } from '@/hooks/useNavigateWithContentFlush';
import { cn } from '@/lib/utils';
import { toContentHref } from '@/utils/content-ref';

type ObligationUnderItem = ContentListObligationsUnderOutput['items'][number];

const HOVER_OPEN_DELAY_MS = 300;
const HOVER_CLOSE_DELAY_MS = 150;

const ROLE_GROUPS: ReadonlyArray<readonly [ObligationRole, string]> = [
  ['ACTIONEE', 'On you'],
  ['ORIGINATOR', 'Waiting on others'],
  ['OUTSTANDING', 'Outstanding'],
];

function headerCounts(rollup: ObligationRollup | null): string {
  if (rollup === null) return 'No open obligations';
  return [
    rollup.actionee > 0 ? `${rollup.actionee} on you` : null,
    rollup.originator > 0 ? `${rollup.originator} waiting on others` : null,
    rollup.outstanding > 0 ? `${rollup.outstanding} outstanding` : null,
  ]
    .filter((part): part is string => part !== null)
    .join(' · ');
}

function itemTitle(item: ObligationUnderItem): string {
  return item.title?.trim() || (item.description ? item.description.trim().slice(0, 60) : '') || item.code;
}

function itemCourt(item: ObligationUnderItem): string {
  if (item.assignee === null) return 'Unassigned';
  return item.assignee.name ?? `Party #${item.assignee.partyId}`;
}

function ObligationUnderRow({ item, onOpen }: { item: ObligationUnderItem; onOpen: (item: ObligationUnderItem) => void }) {
  const due = item.dueAt === null ? null : new Date(item.dueAt);
  return (
    <button
      type="button"
      className="flex w-full gap-2 rounded px-1.5 py-1 text-left hover:bg-accent"
      onClick={() => onOpen(item)}
    >
      <ObligationRoleIcon role={item.role} className="mt-0.5 h-4 w-4" />
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className={cn('shrink-0 rounded-full border px-1.5 text-[10px] font-medium', KIND_CLASSES[item.kind])}>
            {item.kind}
          </span>
          <span className="truncate text-sm">{itemTitle(item)}</span>
        </span>
        <span className="text-xs text-muted-foreground">
          {itemCourt(item)}
          {due !== null ? (
            <>
              {' · '}
              <span className={cn(due.getTime() < Date.now() && 'text-destructive')}>
                {due.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
              </span>
            </>
          ) : null}
        </span>
      </span>
    </button>
  );
}

export function ObligationBadge({
  contentId,
  rollup,
  reserveSpace = false,
}: {
  contentId: number;
  rollup: ObligationRollup | null;
  reserveSpace?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const timerRef = useRef<number | null>(null);
  const contentMatch = useMatch('/content/:contentRef');
  const navigateWithFlush = useNavigateWithContentFlush();
  const query = useObligationsUnder(contentId, open && rollup !== null);

  const clearTimer = () => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };
  const schedule = (next: boolean, delayMs: number) => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      setOpen(next);
    }, delayMs);
  };
  useEffect(() => clearTimer, []);

  if (rollup === null) {
    return <ObligationRoleIcon role={null} reserveSpace={reserveSpace} />;
  }

  const onHoverEnter = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    if (open) clearTimer();
    else schedule(true, HOVER_OPEN_DELAY_MS);
  };
  const onHoverLeave = (event: React.PointerEvent) => {
    if (event.pointerType !== 'mouse') return;
    schedule(false, HOVER_CLOSE_DELAY_MS);
  };
  const stop = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };
  const onTriggerClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();
    clearTimer();
    setOpen((value) => !value);
  };

  const openItem = (item: ObligationUnderItem) => {
    clearTimer();
    setOpen(false);
    const ref = contentMatch?.params.contentRef;
    if (ref === item.source.code || ref === String(item.source.contentId)) {
      window.dispatchEvent(new CustomEvent(MESSAGE_OPEN_OBLIGATIONS_EVENT));
      if (item.highlight) {
        window.dispatchEvent(
          new CustomEvent<MessageHighlightQuoteDetail>(MESSAGE_HIGHLIGHT_QUOTE_EVENT, { detail: item.highlight })
        );
      }
      return;
    }
    navigateWithFlush(toContentHref(item.source.code), {
      state: { highlight: item.highlight, openObligations: true },
    });
  };

  const items = query.data?.items ?? [];

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        clearTimer();
        setOpen(next);
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="Obligations breakdown"
          className="inline-flex shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={onTriggerClick}
          onPointerDown={stop}
          onKeyDown={stop}
          onPointerEnter={onHoverEnter}
          onPointerLeave={onHoverLeave}
        >
          <ObligationRoleIcon role={rollup.role} />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="max-h-[60vh] w-80 overflow-y-auto p-3"
        onOpenAutoFocus={(event) => event.preventDefault()}
        onPointerEnter={onHoverEnter}
        onPointerLeave={onHoverLeave}
        onClick={stop}
        onPointerDown={stop}
      >
        <p className="mb-2 text-xs font-medium">{headerCounts(query.data ? query.data.rollup : rollup)}</p>
        {query.isPending ? (
          <p className="text-xs text-muted-foreground">Loading…</p>
        ) : query.isError ? (
          <p className="text-xs text-destructive">Couldn&apos;t load obligations.</p>
        ) : (
          <div className="flex flex-col gap-2">
            {ROLE_GROUPS.map(([role, label]) => {
              const group = items.filter((item) => item.role === role);
              if (group.length === 0) return null;
              return (
                <div key={role} className="flex flex-col gap-0.5">
                  <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
                  {group.map((item) => (
                    <ObligationUnderRow key={item.contentId} item={item} onOpen={openItem} />
                  ))}
                </div>
              );
            })}
            {query.data?.truncated ? (
              <p className="text-[11px] text-muted-foreground">Showing first 50</p>
            ) : null}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
